import { Box, Button, Grid, Link, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getLinks, handleErrorMessages } from '../../../helpers';
import { RequiredFormData } from '../formTypes';
import { LoginWithGoogle } from './LoginWithGoogle';
import { languages, LinkType } from '../../../constans';
import { theme } from '../../../theme';
import { Input } from './Input';
import { LoaderComponent } from '../LoaderComponent';

type Props = {
    initData: any;
    setRequiredData: (data: RequiredFormData & { captcha: string | null | undefined }) => void;
    captchaRef: React.MutableRefObject<ReCAPTCHA | undefined>;
  };
  
  export const AccountDetailsForm: React.FC<Props> = ({
    initData,
    setRequiredData,
    captchaRef,
  }) => {
    const { language } = useParams();
    const defaultValues = {
        email: '',
        firstName: '',
        lastName: '',
        companyName: '',
        language: language
    };
    const { t } = useTranslation();
    const [isServerError, setServerError] = React.useState(false);
    const { handleSubmit, setValue, setError, reset, register, formState: { errors, isSubmitting, isValid } } = useForm<RequiredFormData>({
      defaultValues,
      mode: 'onChange',
    });
  
    const onSubmit = React.useCallback(
      async (data: RequiredFormData) => {
        const token = await captchaRef.current?.executeAsync();
        captchaRef.current?.reset();
        try {
          const postResponse = await window.fetch('/Registration/Check', {
            method: 'POST',
            mode: 'cors',
            headers: {
              'content-type': 'application/json;charset=UTF-8'
            },
            body: JSON.stringify({ ...data, captcha: token })
          });
  
          if (postResponse.ok) {
            setRequiredData({ ...data, captcha: token });
            reset();
          } else if (postResponse.status === 400) {
            const responseText = JSON.parse(await postResponse.text());
            handleErrorMessages(responseText['errors'], (field, message) => {
              setError(field, { type: 'custom', message: message });
            }, () => setServerError(true));
          } else {
            setServerError(true);
          }
        } catch (e) {
          console.error(e);
          setServerError(true);
        }
      },
      [captchaRef, reset, setError, setRequiredData]
    );
  
    return (
      <>
        {isSubmitting && <LoaderComponent />}
        <form onSubmit={handleSubmit(onSubmit)} style={{ visibility : isSubmitting ? 'hidden' : 'visible'}}>
          {isServerError &&
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '1rem' }}>
              <ErrorOutlineIcon sx={{ mr: '0.5rem' }} color="error" />
              <Typography pb="0px" color={theme.palette.error.main} variant="body2">
                {t('errors.unknownError')}
              </Typography>
            </Box>
          }
          <Typography height="min-content" mb="0.4rem" pb="0px" textAlign={'left'} variant="body2" fontSize={'10px'}>
            {t('infoText.registration')}
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
                <Input
                  errors={errors}
                  register={register}
                  name="email"
                  rules={{
                    required: true,
                    pattern: /^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/
                  }}
                  label={t('fields.email')}
                  tooltip={{
                    title: t('tooltips.email'),
                    placement: 'top'
                  }}
                />
            </Grid>
            <Grid item xs={6}>
              <Input
                  errors={errors}
                  register={register}
                  name="firstName"
                  rules={{ 
                    required: true,
                    minLength: 2,
                    pattern: /^[^\s]+(\s+[^\s]+)*$/,
                  }}
                  label={t('fields.firstName')}
              />
            </Grid>
            <Grid item xs={6}>
              <Input
                  errors={errors}
                  register={register}
                  name="lastName"
                  rules={{
                    required: true,
                    minLength: 2,
                    pattern: /^[^\s]+(\s+[^\s]+)*$/ 
                  }}
                  label={t('fields.lastName')}
              />
            </Grid>
            <Grid item xs={6}>
              <Input
                  errors={errors}
                  register={register}
                  name="companyName"
                  rules={{
                    required: true,
                    minLength: 2,
                    pattern: /^[^\s]+(\s+[^\s]+)*$/
                  }}
                  label={t('fields.companyName')}
              />
            </Grid>
            <Grid item xs={6}>
              <Input
                  errors={errors}
                  register={register}
                  name="language"
                  rules={{ required: true }}
                  label={t('fields.language')}
                  select
                  options={languages}
                  defaultValue={defaultValues.language}
              />
            </Grid>
          </Grid>
          <Box
          mt="1rem"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'left', width: '100%', marginBottom: '5px' }}>
            <Typography variant="body2" fontSize={11}>
              {t('infoText.gdpr1')}{' '}
              <Link
                component="a"
                variant="body2"
                fontSize={11}
                color="rgba(129, 135, 143, 1)"
                href={getLinks(LinkType.gdpr, language)}
                target="_blank"
                rel="noreferrer"
              >
                {t('infoText.gdpr2')}
              </Link>
            </Typography>
          </Box>
          <Button
            sx={{ maxWidth: '100%', margin: 'auto', textTransform: 'none', color: 'white' }}
            fullWidth
            variant="contained"
            color="success"
            type='submit'
          >
            {t('buttonLabels.continue')}
          </Button>

          {initData?.clientId && (
            <LoginWithGoogle
              successFn={(data: { email: string; givenName: string; familyName: string }) => {
                setValue('email', data.email);
                setValue('firstName', data.givenName);
                setValue('lastName', data.familyName);
              }}
              clientId={initData?.clientId}
            />
          )}
          <Box
            sx={{
              display: { lg: 'none', md: 'none', xs: 'flex', sm: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '5px'
            }}
          >
            <Typography variant="body2">
              {t('header.alreadyRegistred')}{' '}
              <Link
                component="a"
                variant="body1"
                href={getLinks(LinkType.logIn, language)}
              >
                {t('header.logIn')}
              </Link>
            </Typography>
          </Box>
        </Box>
        </form>
      </>
    );
}